import { CameraOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { Channel, ChannelDoc, GoodunclesService } from '@gooduncles/gu-app-schema';
import type { ChannelMode, ChannelType, PushSenderCollection } from '@gooduncles/gu-app-schema';
import { Avatar, Button, Form, Input, Modal, Select, Space, Tag, Tooltip, Typography, Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { FC, useEffect, useState } from 'react';

import { formatDate } from 'src/utils/date-util';
import { createChannel, getDownloadURL, updateChannel, uploadFile } from 'src/utils/firebase/firebase-short-cut';

import styles from './ChannelForm.module.scss';

const { Text } = Typography;

interface ChannelFormProps {
  open: boolean;
  onClose: () => void;
  editingChannel: ChannelDoc | null;
}

interface FormValues {
  name: string;
  docId?: string;
  type: ChannelType;
  mode: ChannelMode;
  service: GoodunclesService;
  allowedSenderCollections: PushSenderCollection[];
  description?: string;
}

const ICON_STORAGE_PATH = 'channel-icon';

const channelTypeOptions = [
  { label: '공지', value: 'notice', desc: '전체 공지 알림' },
  { label: '프로모션', value: 'promotion', desc: '광고/프로모션' },
  { label: '매장', value: 'store', desc: '매장별 개별 채널' },
];

const channelModeOptions = [
  { label: '단방향 (broadcast)', value: 'broadcast', desc: '관리자 → 사용자' },
  { label: '양방향 (conversation)', value: 'conversation', desc: '관리자 ↔ 사용자' },
];

const serviceOptions = Object.values(GoodunclesService).map((s) => ({
  label: s,
  value: s,
}));

const senderCollectionOptions: { label: string; value: PushSenderCollection }[] = [
  { label: 'user', value: 'user' },
  { label: 'store', value: 'store' },
  { label: 'system', value: 'system' },
  { label: 'ai', value: 'ai' },
];

const ChannelForm: FC<ChannelFormProps> = ({ open, onClose, editingChannel }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!editingChannel;

  useEffect(() => {
    if (open) {
      if (editingChannel) {
        form.setFieldsValue({
          name: editingChannel.name,
          type: editingChannel.type,
          mode: editingChannel.mode,
          service: editingChannel.service,
          allowedSenderCollections: editingChannel.allowedSenderCollections,
          description: editingChannel.description,
        });
        setIconUrl(editingChannel.iconImageUrl ?? null);
      } else {
        form.resetFields();
        setIconUrl(null);
      }
    }
  }, [open, editingChannel, form]);

  const handleIconUpload = async (file: RcFile): Promise<false> => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      message.error('JPG, PNG, WebP 이미지만 업로드 가능합니다.');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('이미지 크기는 5MB 이하여야 합니다.');
      return false;
    }

    setUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storagePath = `${ICON_STORAGE_PATH}/${fileName}`;
      const result = await uploadFile(storagePath, file, {
        contentType: file.type,
        cacheControl: 'public,max-age=864000',
      });
      const url = await getDownloadURL(result.ref);
      setIconUrl(url);
    } catch (error) {
      console.error('Failed to upload icon image:', error);
      message.error('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isEdit) {
        await updateChannel(editingChannel._id, {
          name: values.name,
          type: values.type,
          mode: values.mode,
          service: values.service,
          allowedSenderCollections: values.allowedSenderCollections,
          description: values.description ?? undefined,
          iconImageUrl: iconUrl ?? undefined,
        });
        message.success('채널이 수정되었습니다.');
      } else {
        const now = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
        const channelData: Channel = {
          name: values.name,
          type: values.type,
          mode: values.mode,
          service: values.service,
          allowedSenderCollections: values.allowedSenderCollections,
          description: values.description ?? undefined,
          iconImageUrl: iconUrl ?? undefined,
          createdAt: now,
          quietHours: null,
          // 메시지가 없는 새 채널은 null로 시작 — 첫 메시지 도착 시 gu-functions 트리거가 객체로 교체한다.
          consoleInbox: null,
          // 라벨 미부여 상태로 생성. 콘솔 받은편지함에서 운영자가 부여한다.
          label: null,
        };
        const docId = values.docId?.trim() || undefined;
        await createChannel(channelData, docId);
        message.success('채널이 생성되었습니다.');
      }
      onClose();
    } catch (error) {
      console.error('Failed to save channel:', error);
      if (error instanceof Error && error.message.includes('이미 존재하는 doc')) {
        message.error('이미 존재하는 문서 ID입니다. 다른 ID를 입력해주세요.');
      } else {
        message.error('채널 저장에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEdit ? '저장' : '생성'}
      cancelText='취소'
      confirmLoading={loading}
      width={520}
      destroyOnClose
      styles={{ body: { paddingTop: 0 } }}
    >
      {/* 헤더: 아이콘 + 채널명 + 설명 */}
      <div className={styles.header}>
        <Upload
          showUploadList={false}
          beforeUpload={handleIconUpload}
          accept='image/jpeg,image/png,image/webp'
          disabled={uploading}
        >
          <Tooltip title='클릭하여 아이콘 변경'>
            <div className={styles.avatarWrapper}>
              {uploading && <Avatar size={72} icon={<LoadingOutlined />} className={styles.avatar} />}
              {!uploading && iconUrl && <Avatar size={72} src={iconUrl} className={styles.avatar} />}
              {!uploading && !iconUrl && <Avatar size={72} icon={<CameraOutlined />} className={styles.avatarEmpty} />}
              <div className={styles.avatarOverlay}>
                <CameraOutlined style={{ fontSize: 16, color: '#fff' }} />
              </div>
            </div>
          </Tooltip>
        </Upload>
        {iconUrl && (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={() => setIconUrl(null)}
            className={styles.removeIconBtn}
          >
            아이콘 삭제
          </Button>
        )}
        <div className={styles.headerMeta}>
          <Text strong style={{ fontSize: 16 }}>
            {isEdit ? '채널 수정' : '새 채널 생성'}
          </Text>
          {isEdit && (
            <Space size={4}>
              <Tag color='default' style={{ fontSize: 11, margin: 0 }}>
                {editingChannel?._id}
              </Tag>
              <Text type='secondary' style={{ fontSize: 11 }}>
                {editingChannel?.createdAt ? formatDate(new Date(editingChannel.createdAt), 'yyyy-MM-dd HH:mm') : ''}
              </Text>
            </Space>
          )}
        </div>
      </div>

      <Form form={form} layout='vertical' requiredMark='optional' className={styles.form}>
        {/* 기본 정보 */}
        <div className={styles.section}>
          <Text type='secondary' className={styles.sectionLabel}>
            기본 정보
          </Text>

          <Form.Item name='name' label='채널명' rules={[{ required: true, message: '채널명을 입력해주세요.' }]}>
            <Input placeholder='예: commerce-notice, store-00001' maxLength={50} showCount />
          </Form.Item>

          {!isEdit && (
            <Form.Item
              name='docId'
              label='문서 ID'
              tooltip='비워두면 자동 생성됩니다. 직접 지정하려면 입력하세요.'
              rules={[
                {
                  pattern: /^(?!\.\.?$)(?!__.*__$)[^/]+$/,
                  message: "'/' 또는 예약어(.., __..__)는 사용할 수 없습니다.",
                },
              ]}
            >
              <Input placeholder='자동 생성 (직접 지정 시 입력)' maxLength={1500} allowClear />
            </Form.Item>
          )}

          <Form.Item name='description' label='설명'>
            <Input.TextArea rows={2} placeholder='채널의 용도를 간단히 설명해주세요.' maxLength={200} showCount />
          </Form.Item>
        </div>

        {/* 채널 설정 */}
        <div className={styles.section}>
          <Text type='secondary' className={styles.sectionLabel}>
            채널 설정
          </Text>

          <div className={styles.row}>
            <Form.Item
              name='type'
              label='유형'
              rules={[{ required: true, message: '유형을 선택해주세요.' }]}
              className={styles.halfField}
            >
              <Select placeholder='유형 선택'>
                {channelTypeOptions.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    <span>{opt.label}</span>
                    <Text type='secondary' style={{ fontSize: 11, marginLeft: 6 }}>
                      {opt.desc}
                    </Text>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name='mode'
              label='모드'
              rules={[{ required: true, message: '모드를 선택해주세요.' }]}
              className={styles.halfField}
            >
              <Select placeholder='모드 선택'>
                {channelModeOptions.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    <span>{opt.label}</span>
                    <Text type='secondary' style={{ fontSize: 11, marginLeft: 6 }}>
                      {opt.desc}
                    </Text>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item name='service' label='메인 서비스' rules={[{ required: true, message: '서비스를 선택해주세요.' }]}>
            <Select options={serviceOptions} placeholder='이 채널을 구독하는 주요 서비스' />
          </Form.Item>

          <Form.Item
            name='allowedSenderCollections'
            label='허용 발송자 유형'
            rules={[{ required: true, message: '최소 1개 이상 선택해주세요.' }]}
            tooltip='이 채널에 메시지를 보낼 수 있는 발송자 유형'
          >
            <Select
              mode='multiple'
              placeholder='발송자 유형 선택'
              tagRender={({ label, closable, onClose: onTagClose }) => (
                <Tag closable={closable} onClose={onTagClose} style={{ marginInlineEnd: 4 }}>
                  {label}
                </Tag>
              )}
            >
              {senderCollectionOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default ChannelForm;
